import { createUrl } from "@/redux/api";
import {
  BaseSurveyViewResponse,
  CreateUpdateSurveyView,
  PreviewSurveyViewRequest,
  SurveyViewResponse,
  SurveyViewDownload,
  SurveyViewPreview,
  SurveyViewQueryParams,
  SurveyViewWithSurvey,
  Tags,
  TagsType,
} from "@/redux/survey-views/types";
import { commonsApi } from "@/redux/service";
import { SurveyResponse } from "@/redux/survey/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { BuilderType, PaginatedResponse } from "@/redux/types";

const surveyViewsService = (builder: BuilderType<Tags>) => ({
  getSurveyViews: builder.query<
    BaseSurveyViewResponse[] | PaginatedResponse<BaseSurveyViewResponse>,
    SurveyViewQueryParams
  >({
    query: (filters = {}) => createUrl("/surveys/views/", filters),
    providesTags: (result): TagsType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as BaseSurveyViewResponse[]
            ).map(
              (i): TagsType => ({
                type: "SurveyView",
                id: i.id,
              }),
            ),
            { type: "SurveyViews" },
          ]
        : [],
  }),
  getSurveyViewsWithSurveys: builder.query<
    SurveyViewWithSurvey[] | PaginatedResponse<SurveyViewWithSurvey>,
    SurveyViewQueryParams
  >({
    async queryFn(
      params,
      { dispatch },
    ): Promise<
      | {
          data:
            | SurveyViewWithSurvey[]
            | PaginatedResponse<SurveyViewWithSurvey>;
        }
      | { error: any }
    > {
      const surveyViewsResult = await dispatch(
        commonsApi?.endpoints?.getSurveyViews.initiate(params),
      ).unwrap();
      const isPaginated = isPaginatedResponse(surveyViewsResult);
      const surveyViews = isPaginated
        ? surveyViewsResult.results
        : (surveyViewsResult as BaseSurveyViewResponse[]);
      const surveyIds = surveyViews.map((view) => view.survey).filter(Boolean);

      const surveys = (await dispatch(
        commonsApi.endpoints.getSurveys.initiate({
          ids: surveyIds,
          pagination: "off",
        }),
      ).unwrap()) as SurveyResponse[];

      const surveyMap = surveys.reduce<Record<number, SurveyResponse>>(
        (acc, survey) => {
          acc[survey.id] = survey;
          return acc;
        },
        {},
      );

      const enhancedResults = surveyViews.map((view) => ({
        ...view,
        survey: surveyMap[view.survey] as SurveyResponse,
      }));

      return {
        data: isPaginated
          ? {
              ...surveyViewsResult,
              results: enhancedResults,
            }
          : enhancedResults,
      };
    },
    providesTags: [{ type: "SurveyViews" }],
  }),
  getSurveyViewById: builder.query<SurveyViewResponse, string>({
    query: (id) => `/surveys/views/${id}/`,
    providesTags: (result) =>
      result
        ? [{ type: "SurveyView", id: result.id }, { type: "SurveyViews" }]
        : [],
  }),
  downloadSurveyView: builder.query<SurveyViewDownload, string>({
    query: (id) => `/surveys/views/${id}/download/`,
    keepUnusedDataFor: 0,
  }),
  viewSurveyView: builder.query<SurveyViewPreview, string>({
    query: (surveyViewId) => `/surveys/views/${surveyViewId}/viewer/`,
    providesTags: (result, error, args) =>
      result ? [{ type: "SurveyView", id: args }, { type: "SurveyViews" }] : [],
  }),
  getSurveyViewPreview: builder.mutation<
    SurveyViewPreview,
    PreviewSurveyViewRequest
  >({
    query: (data) => ({
      url: "/surveys/views/preview/",
      method: "POST",
      data,
    }),
  }),
  createSurveyView: builder.mutation<
    SurveyViewResponse,
    CreateUpdateSurveyView
  >({
    query: (data) => ({
      url: "/surveys/views/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result ? [{ type: "SurveyViews" }, { type: "DataBlockVersions" }] : [],
  }),
  updateSurveyView: builder.mutation<
    SurveyViewResponse,
    { id: string; data: CreateUpdateSurveyView }
  >({
    query: ({ id, data }) => ({
      url: `/surveys/views/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "SurveyView", id: result.id },
            { type: "SurveyViews" },
            { type: "DataBlockVersions" },
          ]
        : [],
  }),
  deleteSurveyView: builder.mutation<null, string>({
    query: (id) => ({
      url: `/surveys/views/${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: (result, error, args) =>
      error ? [] : [{ type: "SurveyView", id: args }, { type: "SurveyViews" }],
  }),
  incrementSurveyViewViews: builder.query<null, string>({
    query: (id) => ({
      url: `/surveys/views/${id}/views/`,
      method: "GET",
    }),
  }),
});

export default surveyViewsService;
